import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fdiToHaderup } from '@/utils/toothNotation';
import type { Database } from '@/types/database.generated';
import { TreatmentDetailDrawer } from './TreatmentDetailDrawer';
import { Button } from '@/components/ui/Button';

type TreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];

// ── Props ─────────────────────────────────────────────────────────────────────

interface HistoryTableProps {
  rows: TreatmentRow[];
  isLoading?: boolean;
}

const PAGE_SIZE = 50;

// ── Loading skeleton ──────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr>
      {[...Array(7)].map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-3 rounded bg-ds-sunk animate-pulse" style={{ width: `${60 + i * 5}%` }} />
        </td>
      ))}
    </tr>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HistoryTable({ rows, isLoading = false }: HistoryTableProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [selectedTreatment, setSelectedTreatment] = useState<TreatmentRow | null>(null);

  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead>
            <tr className="bg-ds-sunk">
              {[
                t('dental.history.columns.date'),
                t('dental.history.columns.tooth'),
                t('dental.history.columns.code'),
                t('dental.history.columns.description'),
                t('dental.history.columns.performer'),
                t('dental.history.columns.clinic'),
                t('dental.history.columns.price'),
              ].map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-ds-muted uppercase tracking-wider whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ds-line bg-ds-card">
            {isLoading ? (
              [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-ds-muted">
                  {t('dental.history.empty')}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelectedTreatment(row)}
                  className="cursor-pointer hover:bg-ds-teal-tint/50 transition-colors"
                >
                  {/* Dato */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-ds-muted">
                    {format(new Date(row.treatment_date), 'd. MMM yyyy', { locale: da })}
                  </td>
                  {/* Tand */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-mono text-sm font-semibold text-ds-text">
                      {row.tooth_fdi}
                    </span>
                    <span className="ml-1 text-xs text-ds-muted">
                      ({fdiToHaderup(row.tooth_fdi)})
                    </span>
                  </td>
                  {/* Kode */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="rounded bg-ds-neutral-tint px-1.5 py-0.5 font-mono text-xs text-ds-muted">
                      {row.treatment_type}
                    </span>
                  </td>
                  {/* Beskrivelse */}
                  <td className="px-4 py-3 max-w-[220px]">
                    <span className="block truncate text-sm text-ds-text">
                      {row.treatment_description}
                    </span>
                  </td>
                  {/* Behandler */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-ds-muted">
                    {row.performed_by}
                  </td>
                  {/* Klinik */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {row.performed_at_other_practice ? (
                      <span className="inline-flex items-center gap-1 text-xs text-ds-muted">
                        <span className="h-2 w-2 rounded-full bg-ds-line-strong flex-shrink-0" />
                        {row.other_practice_name
                          ? `Anden klinik · ${row.other_practice_name}`
                          : 'Anden klinik'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-ds-teal">
                        <span className="h-2 w-2 rounded-full bg-ds-teal flex-shrink-0" />
                        Egen
                      </span>
                    )}
                  </td>
                  {/* Pris */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-ds-text text-right">
                    {row.actual_cost != null
                      ? `${row.actual_cost.toLocaleString('da-DK')} kr`
                      : row.estimated_cost != null
                        ? `~${row.estimated_cost.toLocaleString('da-DK')} kr`
                        : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-ds-line">
          <span className="text-xs text-ds-muted">
            Side {page + 1} af {totalPages} ({rows.length} poster)
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}

      <TreatmentDetailDrawer
        treatment={selectedTreatment}
        onClose={() => setSelectedTreatment(null)}
      />
    </>
  );
}
